import {
  TIP_DISMISSALS_TO_NOT_RELEVANT,
  formatGroupedNumber,
  formatJalaliLong,
  formatToman,
  toPersianDigits,
} from '@bookora/shared';

import type { GuideTextValueResponse, GuideTipResponse } from '@/api/generated/model';

import { MENU, UNDO } from './copy';

/**
 * Small pure decisions about one tip, shared by the card, the hub rows and the
 * inline slot (kept out of the component modules for Fast Refresh).
 */

/** What the ⋯ menu can ask for. */
export type TipMenuAction = 'SNOOZE_DAY' | 'SNOOZE_WEEK' | 'NOT_RELEVANT' | 'MARK_DONE';

/** Every action that takes a tip off the list, and so leaves an undo row. */
export type HidingAction = TipMenuAction | 'DISMISS';

/**
 * The manual-done label: the rule's own MARK_DONE button wording when it has
 * one («خودم فرستادم», «باز هستیم»), else «انجام دادم» — and, for a tip the
 * whole clinic shares, a wording that says it is marked for everyone.
 */
export function markDoneLabel(tip: GuideTipResponse): string {
  const own = [tip.cta, ...tip.secondaryCtas].find((cta) => cta.kind === 'MARK_DONE')?.label;
  if (own) return own;
  return tip.scope === 'CLINIC' ? MENU.markDoneClinic : MENU.markDone;
}

/** Manual done is offered only where the server allows it (else it answers 400). */
export function canMarkDone(tip: GuideTipResponse): boolean {
  return tip.completionMode === 'MANUAL' || tip.allowManualDone;
}

/*
 * Two fields the guide engine adds to a tip: `why`, a short «چون …» line with
 * the same `{tokens}` as the body, and `dismissCount`, how often this person
 * already closed it. Both are optional on the wire (a server released before
 * them, a panel deployed alone with `--only=clinic`), so read them here only.
 */

/** The tip's one-line reason («چون ۱۲ بیمار …»), or `null` when the rule has none. */
export function tipWhy(tip: GuideTipResponse): string | null {
  const why = tip.why;
  return typeof why === 'string' && why.trim() !== '' ? why : null;
}

/**
 * Whether «×» retires this tip for good — the shared fold turns the
 * `TIP_DISMISSALS_TO_NOT_RELEVANT`-th dismissal into «به کار ما نمی‌آید» — so
 * the undo row says so instead of «بسته شد». Unknown (an older server) = no.
 */
export function dismissRetires(tip: GuideTipResponse): boolean {
  const count = tip.dismissCount;
  return typeof count === 'number' && count + 1 >= TIP_DISMISSALS_TO_NOT_RELEVANT;
}

/** The undo row's sentence for a hiding action. */
export function undoMessage(tip: GuideTipResponse, action: HidingAction): string {
  switch (action) {
    case 'SNOOZE_DAY':
      return UNDO.SNOOZE_DAY;
    case 'SNOOZE_WEEK':
      return UNDO.SNOOZE_WEEK;
    case 'NOT_RELEVANT':
      return UNDO.NOT_RELEVANT;
    case 'MARK_DONE':
      return tip.scope === 'CLINIC' ? UNDO.MARK_DONE_CLINIC : UNDO.MARK_DONE;
    case 'DISMISS':
    default:
      return dismissRetires(tip) ? UNDO.DISMISS_RETIRES : UNDO.DISMISS;
  }
}

/**
 * A template as a plain string — for an accessible name («کارهای دیگر برای
 * «…»») or a confirm title, where no markup is possible. Digits are Persian;
 * a missing value leaves nothing, as in the rendered text.
 */
export function plainGuideText(
  template: string,
  values: readonly GuideTextValueResponse[],
): string {
  const byToken = new Map(values.map((value) => [value.token, value]));
  return template
    .replace(/\{(\w+)\}/g, (_, token: string) => {
      const value = byToken.get(token);
      if (!value) return '';
      switch (value.kind) {
        case 'COUNT':
          return value.number === null || value.number === undefined
            ? ''
            : formatGroupedNumber(value.number);
        case 'MONEY_RIALS':
          return value.number === null || value.number === undefined
            ? ''
            : formatToman(value.number);
        case 'DATE':
          return value.date ? formatJalaliLong(new Date(value.date)) : '';
        case 'PERCENT_BP':
          return value.number === null || value.number === undefined
            ? ''
            : `${toPersianDigits(Math.round(value.number / 100))}٪`;
        case 'TEXT':
          return value.text ?? '';
        default:
          return '';
      }
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** The tip's title as plain text (titles may carry a live count). */
export function tipTitleText(tip: GuideTipResponse): string {
  return plainGuideText(tip.title, tip.values);
}
